import { useEffect, useRef, useState } from 'react';
import { GoldButton } from './Shared';
import { fmtDay } from '../campaign';
import { haptic } from '../../Helper/native';
import { fillCount, isSurveyEnded, surveyProgress } from '../survey';
import artSurvey from '../assets/art_survey.webp';
import chestc from '../assets/chestc.webp';

/* ------------------------------------------------------------------
   "Bonus rewards" · the quick-survey card on the hub, laid out like a
   streak card (prototype .card.svcard). States from `state.status`:
   - new    : "Free Chest" chip, the reward line, "Take the survey"
   - part   : "In progress" chip, how far they got (mini bars), "Resume survey"
   - done   : "Claimed" chip, "Golden Chest claimed", outlined done CTA
   - ended  : past `survey.endsAt` with no `status` in the response (or still
              "new") — "Ended" chip, "This task has ended", and "Notify me
              about new tasks", as Quote & Claim does once its window closes
   `survey` is the mapped API survey (copy + questions); `onStart` opens
   the survey screen. `notify` is the new-tasks reminder; `onNotify(on)`
   toggles it.
   ------------------------------------------------------------------ */

/* "{date}" in the ended copy */
const fill = (s, vars) => String(s || '').replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m));

/* ticks once a second while the window is still open, so the card flips to
   ended on the close without a reload */
function useNow(on) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [on]);
  return now;
}

const Clock = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="6.3" stroke="#F6D5AC" strokeWidth="1.5" /><path d="M8 4.8V8l2.2 1.4" stroke="#F6D5AC" strokeWidth="1.5" strokeLinecap="round" /></svg>
);
const Bell = () => (
  <svg className="gq-xn-bell" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 2.2a4 4 0 0 0-4 4v2.3L2.8 10.6c-.3.5 0 1 .6 1h9.2c.6 0 .9-.5.6-1L12 8.5V6.2a4 4 0 0 0-4-4Z" fill="currentColor" /><path d="M6.4 12.8a1.7 1.7 0 0 0 3.2 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
);

/* "Let's make your money work for you" → "…for you." */
const sentence = (s) => (s && !/[.!?…]$/.test(s) ? `${s}.` : s || '');

export default function SurveyCard({ survey, state, notify = false, onStart, onNotify }) {
  const { copy } = survey;
  const watching = state.status === 'new' && !!survey.endsAt;
  const now = useNow(watching);
  const ended = isSurveyEnded(survey, state, now);
  const [ring, setRing] = useState(false);
  const alive = useRef(true);
  const timers = useRef([]);
  useEffect(() => {
    alive.current = true;
    const pending = timers.current;
    return () => { alive.current = false; pending.forEach(clearTimeout); };
  }, []);
  const later = (fn, ms) => { timers.current.push(setTimeout(() => { if (alive.current) fn(); }, ms)); };
  /* the bell rings first, then the button settles into "You'll be notified" */
  const toggleNotify = () => {
    const on = !notify;
    if (on) {
      setRing(true);
      haptic('impactLight');
      later(() => setRing(false), 700);
      later(() => onNotify(true), 380);
    } else {
      onNotify(false);
    }
  };
  const done = !ended && state.status === 'done';
  const part = !ended && state.status === 'part';
  const pr = part ? surveyProgress(survey, state) : null;

  let chip;
  if (ended) {
    chip = <span>Ended</span>;
  } else if (done) {
    chip = (
      <>
        <svg viewBox="0 0 11 11" fill="none" aria-hidden="true"><path d="M2 5.6l2.3 2.3L9 3.4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        <span>Claimed</span>
      </>
    );
  } else if (part) {
    chip = <><span className="gq-pdot" /><span>In progress</span></>;
  } else {
    chip = <><img src={chestc} alt="" /><span>Free Chest</span></>;
  }

  return (
    <div className={`gq-card gq-svcard${done ? ' gq-done' : ''}${part ? ' gq-part' : ''}${ended ? ' gq-svexp' : ''}`}>
      <div className="gq-tile"><img src={artSurvey} alt="" /></div>
      <div className="gq-t gq-ttl">Quick survey</div>
      <div className="gq-txt">
        <div className="gq-desc">{part ? sentence(fillCount(copy.resumeTitle, pr.rem)) : sentence(copy.title)}</div>
        <div className="gq-fine">{part ? 'Your answers are saved.' : sentence(copy.duration)}</div>
      </div>
      <div className="gq-lockchip gq-svchip">{chip}</div>
      <div className="gq-divider" />

      {ended ? (
        <>
          <div className="gq-xstat gq-ex">
            <div className="gq-xi"><Clock /></div>
            <div>
              <b>{copy.endedTitle}</b>
              <span>{fill(copy.endedText, { date: survey.endsAt ? fmtDay(survey.endsAt) : '' })}</span>
            </div>
          </div>
          <button type="button" className={`gq-xnotify${notify ? ' gq-set' : ''}${ring ? ' gq-ring' : ''}`} onClick={toggleNotify} aria-pressed={notify}>
            {notify
              ? <svg className="gq-xn-tick" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2.5 6.3l2.4 2.4L9.6 3.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              : <Bell />}
            <span>{notify ? copy.notified : copy.notify}</span>
          </button>
          <div className="gq-xnote">{notify ? copy.noteSet : copy.note}</div>
        </>
      ) : (
        <>
          <div className="gq-rwd">
            {part ? (
              <>
                <div className="gq-mini" aria-hidden="true">
                  {Array.from({ length: pr.total }, (_, k) => <i key={k} className={k < pr.done ? 'gq-on' : ''} />)}
                </div>
                <span>{pr.done} of {pr.total} answered · Golden Chest waiting</span>
              </>
            ) : (
              <>
                <img className="gq-ci" src={chestc} alt="" />
                <span>{done ? 'Golden Chest claimed' : copy.reward}</span>
              </>
            )}
          </div>

          {done
            ? <GoldButton done>Survey done · Thank you!</GoldButton>
            : <GoldButton onClick={onStart}>{part ? copy.resumeCta : 'Take the survey'}</GoldButton>}
        </>
      )}
    </div>
  );
}
